import { Component, inject, signal, computed } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NotificationService } from '../../../core/services/notification.service';
import { Notification } from '../../../core/models';

@Component({
  selector: 'app-notification-panel',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, DatePipe],
  template: `
    <!-- Backdrop -->
    @if (open()) {
      <div class="panel-backdrop" (click)="close()"></div>
    }

    <aside class="panel" [class.panel--open]="open()">
      <!-- Header -->
      <div class="panel-header">
        <h2 class="panel-title">Notifications</h2>
        <div class="panel-actions">
          @if (notificationService.unreadCount() > 0) {
            <button class="mark-all-btn" (click)="notificationService.markAllAsRead()">Mark all as read</button>
          }
          <button mat-icon-button class="close-btn" (click)="close()">
            <mat-icon fontSet="material-icons-outlined">close</mat-icon>
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="panel-body">
        @if (notificationService.notifications().length === 0) {
          <div class="empty-state">
            <mat-icon class="empty-icon" fontSet="material-icons-outlined">notifications_none</mat-icon>
            <p class="empty-text">All caught up!</p>
          </div>
        } @else {
          @for (group of groups(); track group.label) {
            <div class="notification-group">
              <div class="group-label">{{ group.label }}</div>
              @for (item of group.items; track item.id) {
                <div
                  class="notification-item"
                  [class.unread]="!item.read"
                  (click)="notificationService.markAsRead(item.id)"
                >
                  @if (!item.read) {
                    <span class="unread-dot"></span>
                  }
                  <div class="notification-icon" [class]="'icon--' + item.type">
                    <mat-icon fontSet="material-icons-outlined">{{ item.icon }}</mat-icon>
                  </div>
                  <div class="notification-content">
                    <p class="notification-title">{{ item.title }}</p>
                    <p class="notification-message">{{ item.message }}</p>
                    <span class="notification-time">{{ item.createdAt | date:'short' }}</span>
                  </div>
                </div>
              }
            </div>
          }
        }
      </div>

      <!-- Footer -->
      <div class="panel-footer">
        <button class="view-all-btn">View all notifications</button>
      </div>
    </aside>
  `,
  styles: `
    :host { display: contents; }

    .panel-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.3);
      z-index: 999;
      animation: fadeIn 0.2s ease;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }

    .panel {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      width: 400px;
      max-width: 100vw;
      background: var(--nf-surface);
      border-left: 1px solid var(--nf-border);
      box-shadow: var(--nf-shadow-lg);
      z-index: 1000;
      display: flex;
      flex-direction: column;
      transform: translateX(100%);
      transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

      &--open {
        transform: translateX(0);
      }
    }

    .panel-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px;
      border-bottom: 1px solid var(--nf-border);
      flex-shrink: 0;
    }

    .panel-title {
      font-size: 18px;
      font-weight: 600;
      color: var(--nf-text);
      margin: 0;
    }

    .panel-actions {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .mark-all-btn {
      background: none;
      border: none;
      color: var(--nf-primary);
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      padding: 4px 8px;
      border-radius: var(--nf-radius-sm);
      transition: background var(--nf-transition);

      &:hover { background: var(--nf-surface-hover); }
    }

    .close-btn {
      color: var(--nf-text-secondary);
    }

    .panel-body {
      flex: 1;
      overflow-y: auto;
      padding: 8px 0;
    }

    /* Group */
    .notification-group {
      &:not(:first-child) {
        margin-top: 4px;
      }
    }

    .group-label {
      font-size: 12px;
      font-weight: 600;
      color: var(--nf-text-muted);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: 8px 20px 4px;
    }

    /* Items */
    .notification-item {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 12px 20px;
      cursor: pointer;
      position: relative;
      transition: background var(--nf-transition);

      &:hover { background: var(--nf-surface-hover); }
      &.unread { background: color-mix(in srgb, var(--nf-primary) 4%, transparent); }
    }

    .unread-dot {
      position: absolute;
      left: 8px;
      top: 20px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--nf-primary);
    }

    .notification-icon {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
        color: #fff;
      }

      &.icon--approval  { background: var(--nf-success); }
      &.icon--document  { background: var(--nf-info); }
      &.icon--memo      { background: var(--nf-primary); }
      &.icon--payment   { background: var(--nf-warning); }
      &.icon--system    { background: var(--nf-text-secondary); }
      &.icon--mention   { background: var(--nf-accent); }
    }

    .notification-content {
      flex: 1;
      min-width: 0;
    }

    .notification-title {
      font-size: 14px;
      font-weight: 600;
      color: var(--nf-text);
      margin: 0 0 2px;
    }

    .notification-message {
      font-size: 13px;
      color: var(--nf-text-secondary);
      margin: 0 0 4px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .notification-time {
      font-size: 12px;
      color: var(--nf-text-muted);
    }

    /* Empty state */
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px 20px;
      gap: 12px;
    }

    .empty-icon {
      font-size: 48px;
      width: 48px;
      height: 48px;
      color: var(--nf-text-muted);
    }

    .empty-text {
      font-size: 16px;
      font-weight: 500;
      color: var(--nf-text-secondary);
      margin: 0;
    }

    /* Footer */
    .panel-footer {
      border-top: 1px solid var(--nf-border);
      padding: 12px 20px;
      flex-shrink: 0;
    }

    .view-all-btn {
      width: 100%;
      background: none;
      border: 1px solid var(--nf-border);
      border-radius: var(--nf-radius);
      padding: 10px;
      font-size: 14px;
      font-weight: 500;
      color: var(--nf-primary);
      cursor: pointer;
      transition: background var(--nf-transition);

      &:hover { background: var(--nf-surface-hover); }
    }
  `,
})
export class NotificationPanelComponent {
  protected readonly notificationService = inject(NotificationService);
  readonly open = signal(false);

  protected readonly groups = computed(() => {
    const items = this.notificationService.notifications();
    if (!items.length) return [];

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterdayStart = new Date(todayStart.getTime() - 86_400_000);

    const today: Notification[] = [];
    const yesterday: Notification[] = [];
    const earlier: Notification[] = [];

    for (const n of items) {
      const d = new Date(n.createdAt);
      if (d >= todayStart) today.push(n);
      else if (d >= yesterdayStart) yesterday.push(n);
      else earlier.push(n);
    }

    const groups: { label: string; items: Notification[] }[] = [];
    if (today.length) groups.push({ label: 'Today', items: today });
    if (yesterday.length) groups.push({ label: 'Yesterday', items: yesterday });
    if (earlier.length) groups.push({ label: 'Earlier', items: earlier });
    return groups;
  });

  toggle(): void {
    this.open.update(v => !v);
  }

  close(): void {
    this.open.set(false);
  }
}
